import classNames from "classnames";
import {
	GridArea,
	GridDimension,
	GridPosition
} from "client/Api/Workspace/common";
import { useStableImperativeCallback } from "client/Hooks";
import { debounce } from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./GridCellPixelValuesTracker.module.less";

export type GridCellPixelValuesKind =
	| "tile"
	| "tile-percentile";

export type GridCellPixelValuesTrackerProps = {
	kind: GridCellPixelValuesKind;
	onChange: (values: GridCellPixelValues) => void;
};

export const GridCellPixelValuesTracker = (props: GridCellPixelValuesTrackerProps): ReactElement => {
	const {
		kind,
		onChange
	} = props;

	const ref: React.RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const stableOnChange: typeof onChange = useStableImperativeCallback(onChange);

	useLayoutEffect(
		(): (() => void) | void => {
			const element: HTMLDivElement | null = ref.current;

			if (!element) {
				stableOnChange(defaultConfiguration);
				return;
			}

			const cancellation = new AbortController();

			const loadFromDom = (): void => {
				const {
					width,
					height,
					marginRight,
					marginBottom
				} = window.getComputedStyle(element);

				// We use parseFloat instead of parseInt because some browser on some zoom levels use values
				// which are a fraction less/more then the defined css values. E.g. in chrome when using 110% zooming 56px is
				// reported as 55.9943px. Using the value the browser uses in it's rendering we are always in sync
				// vs what we expressed in our css (e.g. parseInt would make 55.9 into 55 and multiply that by the gutters +
				// column width, you end up with quiet some misalignment)
				const columnWidth: number = parseFloat(width);
				const rowHeight: number = parseFloat(height);
				const columnGutter: number = parseFloat(marginRight);
				const rowGutter: number = parseFloat(marginBottom);

				const value = new GridCellPixelValues(
					columnWidth,
					rowHeight,
					columnGutter,
					rowGutter
				);

				stableOnChange(value);
			};

			element.addEventListener(
				"transitionend",
				debounce(
					loadFromDom,
					200,
					cancellation.signal
				),
				{ signal: cancellation.signal }
			);

			// do the initial setting of the configuration manually
			loadFromDom();

			return (): void => cancellation.abort();
		},
		[stableOnChange]
	);

	return (
		<div
			ref={ref}
			className={
				classNames(
					styles.root,
					kindToClassMap[kind]
				)
			}
		/>
	);
};
GridCellPixelValuesTracker.displayName = "GridCellPixelValuesTracker";

const kindToClassMap: { [kind in GridCellPixelValuesKind]: string; } = {
	"tile": styles.root_tile,
	"tile-percentile": styles.root_tilePercentile
};

export function useGridCellPixelValuesState(): [GridCellPixelValues, (value: GridCellPixelValues) => void] {
	const [value, setValue] = useState<GridCellPixelValues>(defaultConfiguration);

	const changeHandler = useCallback(
		(value: GridCellPixelValues): void => {
			setValue(
				x => value.equals(x)
					? x
					: value
			);
		},
		[]
	);

	return useMemo(
		(): [GridCellPixelValues, (value: GridCellPixelValues) => void] => [value, changeHandler],
		[value, changeHandler]
	);
}

export class GridCellPixelValues {

	private readonly _columnWidth: number;
	private readonly _rowHeight: number;
	private readonly _columnGutter: number;
	private readonly _rowGutter: number;
	private readonly _columnWidthWithGutter: number;
	private readonly _rowHeightWithGutter: number;

	public get columnWidth(): number {
		return this._columnWidth;
	}
	public get rowHeight(): number {
		return this._rowHeight;
	}
	public get columnGutter(): number {
		return this._columnGutter;
	}
	public get rowGutter(): number {
		return this._rowGutter;
	}
	public get columnWidthWithGutter(): number {
		return this._columnWidthWithGutter;
	}
	public get rowHeightWithGutter(): number {
		return this._rowHeightWithGutter;
	}

	constructor(
		columnWidth: number,
		rowHeight: number,
		columnGutter: number,
		rowGutter: number
	) {
		this._columnWidth = columnWidth;
		this._rowHeight = rowHeight;
		this._columnGutter = columnGutter;
		this._rowGutter = rowGutter;
		this._columnWidthWithGutter = columnWidth + columnGutter;
		this._rowHeightWithGutter = rowHeight + rowGutter;
	}

	public equals(other: GridCellPixelValues) {
		if (!other) {
			return false;
		}
		return this.columnWidth === other.columnWidth
			&& this.columnGutter === other.columnGutter
			&& this.rowHeight === other.rowHeight
			&& this.rowGutter === other.rowGutter;
	}
}

const defaultConfiguration = new GridCellPixelValues(
	56,
	56,
	8,
	8
);

export type GridAreaInPx = {
	left: number;
	top: number;
	width: number;
	height: number;
};

export function convertAreaToPx(
	area: GridArea,
	pixelValues: GridCellPixelValues
): GridAreaInPx {
	return {
		...convertPositionToPx(area.position, pixelValues),
		...convertDimensionToPx(area.dimension, pixelValues),
	};
}

export type GridPositionInPx = {
	left: number;
	top: number;
};

export function convertPositionToPx(
	position: GridPosition,
	pixelValues: GridCellPixelValues
): GridPositionInPx {
	const {
		columnWidthWithGutter,
		rowHeightWithGutter
	} = pixelValues;

	return {
		left: position.column * columnWidthWithGutter,
		top: position.row * rowHeightWithGutter
	};
}

export type GridDimensionInPx = {
	width: number;
	height: number;
};

export function convertDimensionToPx(
	dimension: GridDimension,
	pixelValues: GridCellPixelValues,
	extra: { rowGutter?: boolean; columnGutter?: boolean; } = {}
): GridDimensionInPx {
	const {
		columnWidthWithGutter,
		rowHeightWithGutter,
		columnGutter,
		rowGutter
	} = pixelValues;

	const { columnSpan, rowSpan } = dimension;

	return {
		width: columnSpan === 0
			? 0
			: (columnSpan * columnWidthWithGutter) - (extra.columnGutter ? 0 : columnGutter),
		height: rowSpan === 0
			? 0
			: (rowSpan * rowHeightWithGutter) - (extra.rowGutter ? 0 : rowGutter)
	};
}
